import React, { useRef, useEffect, useState } from "react";
import "./style/DragNDrop.scss";
import classNames from "classnames";
import { useTranslation } from 'react-i18next';

const DefaultTitle = () => { const { t } = useTranslation('common'); return <>{t('file.uploadImage')}</>; };

function DragNDrop({
  onUpload,
  children,
  count,
  formats,
  title,
  label,
  openDialogOnClick,
}) {
  const drop = useRef(null);
  const drag = useRef(null);
  const input = useRef(null);
  const [dragging, setDragging] = useState(false);
  const { t } = useTranslation('common');

  const [message, setMessage] = useState({
    show: false,
    text: null,
    type: null,
  });

  const showMessage = (text, type, timeout) => {
    setMessage({ show: true, text, type });
    setTimeout(() => setMessage({ show: false, text: null, type: null }), timeout);
  };

  const handleInputChange = (e) => {
    const files = [...e.target.files];
    if (validateFiles(files)) {
      onUpload(files);
      showMessage("Ok", "success", 1000);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    const files = [...e.dataTransfer.files];
    if (validateFiles(files)) {
      onUpload(files);
      showMessage("Ok", "success", 1000);
    }
  };

  const validateFiles = (files) => {
    if (count && count < files.length) {
      showMessage(t('file.onlyCountFilesAllowed', { count }), "error", 2000);
      return false;
    }

    if (
      formats &&
      files.some(
        (file) =>
          !formats.some((format) =>
            file.name.toLowerCase().endsWith(format.toLowerCase())
          )
      )
    ) {
      showMessage(
        t('file.onlyFormatsAllowed', { formats: formats.join(", ") }),
        "error",
        2000
      );
      return false;
    }

    return true;
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.target !== drag.current) setDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.target === drag.current) setDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  useEffect(() => {
    const dropEl = drop.current;
    dropEl.addEventListener("dragover", handleDragOver);
    dropEl.addEventListener("drop", handleDrop);
    dropEl.addEventListener("dragenter", handleDragEnter);
    dropEl.addEventListener("dragleave", handleDragLeave);

    return () => {
      dropEl.removeEventListener("dragover", handleDragOver);
      dropEl.removeEventListener("drop", handleDrop);
      dropEl.removeEventListener("dragenter", handleDragEnter);
      dropEl.removeEventListener("dragleave", handleDragLeave);
    };
  }, []);

  return (
    <div
      ref={drop}
      className="DragNDrop"
      style={{ position: "relative" }}
    >
      {openDialogOnClick && (
        <input
          ref={input}
          type="file"
          name="fileElement"
          accept="image/*"
          multiple={!count || count > 1}
          onChange={handleInputChange}
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            opacity: 0,
            cursor: "pointer",
            zIndex: 2,
          }}
        />
      )}

      {label && (
        <div
          className={
            dragging
              ? "DragNDrop__label--placeholder"
              : message.show
              ? `DragNDrop__label--${message.type}`
              : "DragNDrop__label"
          }
        >
          {label}
        </div>
      )}

      {message.show && (
        <div
          className={classNames(
            "DragNDrop__placeholder",
            `DragNDrop__placeholder--${message.type}`
          )}
        >
          {message.text}
        </div>
      )}

      {dragging && (
        <div className="DragNDrop__placeholder" ref={drag}>
          {t('file.dragFile')}
        </div>
      )}

      {children ?? (
        <div className="DragNDrop__area">{title ?? <DefaultTitle />}</div>
      )}
    </div>
  );
}

export default DragNDrop;